import { Dispatch } from 'redux';
import { ActionTypes } from './actions';
import { ReduxAction } from '../actions';
import { ReduxState } from '../state';
import { UserPilotWindowMethods } from '../../web/containers/Userpilot/types';
import { selectUserpilotScriptLoaded } from './selectors';
import { selectIsAdminOfCurrentCourse } from '../Courses/selectors';
import { UserpilotTrackingMeta } from './types';
import { currentUserIdSelector } from '../Auth/selectors';
import { USERPILOT_NON_COURSE_ADMIN_ID } from './constants';

declare global {
  interface Window {
    userpilot: UserPilotWindowMethods;
  }
}

export const setUserpilotScriptLoaded = (): ReduxAction => ({
  type: ActionTypes.USERPILOT_SCRIPT_LOADED,
});

export const reloadUserpilot = () => (
  dispatch: Dispatch<ReduxAction>,
  getState: () => ReduxState
): void => {
  if (
    selectUserpilotScriptLoaded(getState()) &&
    selectIsAdminOfCurrentCourse(getState()) &&
    window.userpilot
  ) {
    window.userpilot.reload();

    dispatch({
      type: ActionTypes.USERPILOT_RELOADED,
    });
  }
};

export const sendUserpilotIdentify = () => (
  dispatch: Dispatch<ReduxAction>,
  getState: () => ReduxState
): void => {
  if (selectUserpilotScriptLoaded(getState()) && window.userpilot) {
    if (selectIsAdminOfCurrentCourse(getState())) {
      const userId = currentUserIdSelector(getState());
      // Note: probably don't need isAdminOfCurrentCourse anymore since we are
      // only call userpilot API for course admins. But keeping it since the
      // flag is still being used in targetting users
      const isAdminOfCurrentCourse = selectIsAdminOfCurrentCourse(getState());
      const lastIdentified = Date.now();

      window.userpilot.identify(userId, {
        isAdminOfCurrentCourse,
        lastIdentified,
      });

      dispatch({
        type: ActionTypes.USERPILOT_IDENTIFY,
        user: {
          id: userId,
          traits: { isAdminOfCurrentCourse, lastIdentified },
        },
      });
    } else {
      window.userpilot.identify(USERPILOT_NON_COURSE_ADMIN_ID);

      // According to userpilot dev, this should clear that cache on the user side
      window.userpilot.clean({ anonymous: 1 });

      dispatch({
        type: ActionTypes.USERPILOT_IDENTIFY,
        user: { id: USERPILOT_NON_COURSE_ADMIN_ID },
      });
    }
  }
};

// Commented sendUserpilotAnonymous out for now, because we don't want to use
// window.userpilot.anonymous() because of userpilot user limit
// export const sendUserpilotAnonymous = () => (
//   dispatch: Dispatch<ReduxAction>,
//   getState: () => ReduxState
// ): void => {
//   if (selectUserpilotScriptLoaded(getState()) && window.userpilot) {
//     window.userpilot.anonymous();

//     dispatch({
//       type: ActionTypes.USERPILOT_ANONYMOUS,
//     });
//   }
// };

export const sendUserpilotTracking = (
  eventName: string,
  meta?: UserpilotTrackingMeta
) => (dispatch: Dispatch<ReduxAction>, getState: () => ReduxState): void => {
  if (
    selectUserpilotScriptLoaded(getState()) &&
    selectIsAdminOfCurrentCourse(getState()) &&
    window.userpilot
  ) {
    window.userpilot.track(eventName, meta);

    dispatch({
      type: ActionTypes.USERPILOT_TRACKING_SENT,
      eventName,
      meta,
    });
  }
};

export const sendUserpilotTrigger = (checkListId: string) => (
  dispatch: Dispatch<ReduxAction>,
  getState: () => ReduxState
): void => {
  if (
    selectUserpilotScriptLoaded(getState()) &&
    selectIsAdminOfCurrentCourse(getState()) &&
    window.userpilot
  ) {
    window.userpilot.trigger(checkListId);

    dispatch({
      type: ActionTypes.USERPILOT_TRIGGER_SENT,
      checkListId,
    });
  }
};
